import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authenticatedFetch } from '../utils/api';
import PresentationCard from '../components/CreateAndLearn/PresentationCard';
import ContentDetail from '../components/CreateAndLearn/ContentDetail';
import AIPresentationGenerator from '../components/CreateAndLearn/AIPresentationGenerator';
import { Loader } from 'lucide-react';
import styles from '../styles/CreateAndLearn.module.css';
import SearchBar from '../components/SearchBar/SearchBar';
import { useDocumentHead } from '../hooks/useDocumentHead';

const CreateAndLearn = () => {
    useDocumentHead(
        'AI Presentation Generator | TopSpeaks',
        'Create professional presentations instantly with AI-powered content generation. Get complete presentations with talking points and visuals in seconds. Generate, customize, and export presentations effortlessly.',
        'index, follow'
    );
    const [selectedContent, setSelectedContent] = useState(null);
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const loadingRef = useRef(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    const loadContent = useCallback(async (pageNum, query, resetExisting = false) => {
        if (loadingRef.current) return;

        try {
            loadingRef.current = true;
            setLoading(true);
            setError(null);

            const endpoint = `/api/ai-presentations/search?${query ?
                `q=${encodeURIComponent(query)}&` :
                ''}page=${pageNum}&per_page=6`;

            const data = await authenticatedFetch(endpoint);

            // Add searchQuery to each item for highlighting
            const items = data.items.map(item => ({
                ...item,
                searchQuery: query
            }));

            if (resetExisting || pageNum === 1) {
                setContent(items);
                window.scrollTo(0, 0);
            } else {
                setContent(prev => [...prev, ...items]);
            }

            setHasMore(items.length > 0 && pageNum < data.total_pages);
            setLoading(false);
        } catch (error) {
            setError(error.message || 'Failed to load presentations');
            console.error('Error loading presentations:', error);
            setLoading(false);
        } finally {
            loadingRef.current = false;
        }
    }, []);

    const handleSearch = async (query) => {
        setIsSearching(true);
        try {
            setSearchQuery(query);
            setPage(1);
            await loadContent(1, query, true);
        } finally {
            setIsSearching(false);
        }
    };

    const handleDelete = async (contentId) => {
        try {
            const response = await authenticatedFetch(`/api/ai-presentations/${contentId}`, {
                method: 'DELETE'
            });

            if (response.success) {
                setContent(prevContent => prevContent.filter(item => item._id !== contentId));
            }
        } catch (error) {
            setError(error.message || 'Failed to delete presentation');
            console.error('Error deleting presentation:', error);
        }
    };

    const handleGeneratedContent = async (newContent) => {
        try {
            const savedContent = await authenticatedFetch('/api/ai-presentations/save', {
                method: 'POST',
                body: { ...newContent, type: 'presentation' }
            });
            setContent(prev => [savedContent, ...prev]);
        } catch (error) {
            console.error('Error saving presentation:', error);
            setError(error.message || 'Failed to save presentation');
        }
    };

    useEffect(() => {
        loadContent(1, searchQuery, true);
    }, [loadContent, searchQuery]);

    const handleScroll = useCallback(() => {
        if (loading || !hasMore || isSearching) return;

        const scrollPosition = window.innerHeight + window.pageYOffset;
        const threshold = document.documentElement.offsetHeight - 200;

        if (scrollPosition >= threshold) {
            setPage(prev => prev + 1);
            loadContent(page + 1, searchQuery);
        }
    }, [loading, hasMore, loadContent, page, isSearching, searchQuery]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const renderContent = () => {

        if (loading && !content.length) {
            return (
                <div className={styles.loadingContainer}>
                    <Loader className={styles.loadingSpinner} />
                </div>
            );
        }

        return (
            <div className={styles.grid}>
                <AIPresentationGenerator onGeneratedContent={handleGeneratedContent} />
                {content.map(item => (
                    <PresentationCard
                        key={item._id || item.id}
                        content={item}
                        onClick={() => setSelectedContent(item)}
                        onDelete={handleDelete}
                    />
                ))}
                {loading && content.length > 0 && (
                    <div className={styles.infiniteScrollLoader}>
                        <div className={styles.loaderContent}>
                            <div className={styles.loadingSpinnerContainer}>
                                <Loader className={styles.loadingSpinner} />
                            </div>
                            <span className={styles.loaderText}>
                                Loading more presentations
                                <span className={styles.loaderDots}></span>
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className={styles.container}>
                {!selectedContent ? (
                    <>
                        <div className={styles.header}>
                            <h1>AI Presentation Generator</h1>
                            <p>Generate complete presentations with talking points and visuals in seconds.</p>
                            <SearchBar onSearch={handleSearch} />
                        </div>

                        {error && (
                            <div className={styles.error}>
                                {error}
                            </div>
                        )}

                        {renderContent()}
                    </>
                ) : (
                    <ContentDetail
                        content={selectedContent}
                        onBack={() => setSelectedContent(null)}
                    />
                )}
            </div>
        </>
    );
};

export default CreateAndLearn;